import STATE_CITY from "./stateCityMapping";

const normalizePlace = (str = "") =>
  String(str)
    .toLowerCase()
    .replace(/[^a-zA-Z\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const findStateCityIds = (stateName, cityName) => {
  const nState = normalizePlace(stateName);
  const nCity = normalizePlace(cityName);

  for (const key in STATE_CITY) {
    const state = STATE_CITY[key];
    const sName = normalizePlace(state.state_name);
    if (nState !== sName) continue;

    const stateId = state.state_id;
    for (const cityId in state.cities) {
      const cName = normalizePlace(state.cities[cityId]);
      if (
        nCity === cName ||
        nCity.includes(cName) ||
        cName.includes(nCity)
      ) {
        return { stateId, cityId };
      }
    }
    return { stateId, cityId: "" };
  }

  return { stateId: "", cityId: "" };
};

const pick = (...values) => {
  for (const value of values) {
    const text = String(value ?? "").trim();
    if (text) return text;
  }
  return "";
};

const flattenAddressObject = (addr) => {
  if (!addr) return "";
  if (typeof addr === "string") return addr.trim();
  if (typeof addr !== "object") return "";
  return [
    addr.house,
    addr.street,
    addr.landmark,
    addr.loc || addr.locality,
    addr.vtc || addr.vtcName,
    addr.dist || addr.district,
    addr.state,
    addr.pc || addr.pincode || addr.pin,
    addr.country,
  ]
    .filter(Boolean)
    .join(", ");
};

const getAddress = (d) => {
  const fromParts = [
    d.careOf || d.care_of,
    d.house,
    d.street,
    d.landmark,
    d.locality || d.loc,
    d.vtcName || d.vtc,
    d.district || d.dist,
    d.state,
    d.pincode || d.pc || d.pin,
    d.country,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    fromParts ||
    flattenAddressObject(d.address) ||
    flattenAddressObject(d.permanentAddress) ||
    pick(d.addressLine, d.fullAddress, typeof d.address === "string" ? d.address : "")
  );
};

/** Pull district/city from a flattened Aadhaar address string when dist field missing */
const extractDistrictFromAddress = (address, stateName = "") => {
  const text = String(address || "").trim();
  if (!text) return "";

  const parts = text
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);
  if (!parts.length) return "";

  const nState = normalizePlace(stateName);
  const cleaned = parts.filter((p) => {
    const n = normalizePlace(p);
    if (!n) return false;
    if (/^\d{6}$/.test(n.replace(/\s/g, ""))) return false;
    if (n === "india" || n === "bharat") return false;
    if (nState && (n === nState || n.includes(nState) || nState.includes(n))) {
      return false;
    }
    return true;
  });

  // Usual Aadhaar order: ... locality, district, state, pin, country
  // After filtering state/pin/country, last remaining chunk is often district.
  if (cleaned.length >= 1) {
    return cleaned[cleaned.length - 1];
  }
  return "";
};

const splitName = (fullName, firstFallback = "", lastFallback = "") => {
  const name = String(fullName || "").trim();
  if (!name) {
    return {
      firstName: firstFallback,
      lastName: lastFallback,
    };
  }
  const parts = name.split(/\s+/);
  return {
    firstName: parts[0] || firstFallback,
    lastName: parts.slice(1).join(" ") || lastFallback,
  };
};

/**
 * After identity verify on /apply-loan — seed profile for LoanForm1
 * (contact step removed; email collected on loan form).
 * Safe to call again on resume / refresh.
 */
export const seedApplyLoanProfileFromAadhaar = () => {
  let aadhaar = {};
  try {
    aadhaar = JSON.parse(localStorage.getItem("aadhaarData") || "{}");
  } catch {
    aadhaar = {};
  }

  let existing = {};
  try {
    existing = JSON.parse(localStorage.getItem("loanFormData") || "{}");
  } catch {
    existing = {};
  }

  const fullName = pick(
    aadhaar.name,
    aadhaar.fullName,
    aadhaar.full_name,
    [aadhaar.firstName, aadhaar.middleName, aadhaar.lastName]
      .filter(Boolean)
      .join(" "),
    existing.pan_fullName,
    [existing.pan_firstName, existing.pan_lastName].filter(Boolean).join(" ")
  );

  const { firstName, lastName } = splitName(
    fullName,
    pick(aadhaar.firstName, existing.firstName, existing.first_name),
    pick(aadhaar.lastName, existing.lastName, existing.last_name, existing.middle_name)
  );

  const mobile =
    String(localStorage.getItem("mobileNumber") || "")
      .replace(/\D/g, "")
      .slice(-10) ||
    String(existing.mobile || "").replace(/\D/g, "").slice(-10);

  const state = pick(
    aadhaar.state,
    aadhaar.address?.state,
    existing.state
  );
  const address = getAddress(aadhaar) || existing.address || existing.pan_address || "";
  const district = pick(
    aadhaar.district,
    aadhaar.dist,
    aadhaar.address?.dist,
    aadhaar.address?.district,
    aadhaar.permanentAddress?.dist,
    aadhaar.permanentAddress?.district,
    existing.district,
    extractDistrictFromAddress(address, state),
    // vtc is village/town — last resort only
    aadhaar.vtcName,
    aadhaar.vtc,
    aadhaar.address?.vtc
  );
  const pincode = pick(
    aadhaar.pincode,
    aadhaar.pc,
    aadhaar.pin,
    aadhaar.address?.pc,
    aadhaar.address?.pincode,
    existing.pincode
  );
  const gender = pick(aadhaar.gender, existing.gender, existing.pan_gender);
  const dob = pick(
    aadhaar.dateOfBirth,
    aadhaar.dob,
    aadhaar.birthDate,
    existing.dob,
    existing.pan_dob
  );

  const { stateId: mappedStateId, cityId: mappedCityId } = findStateCityIds(
    state,
    district
  );
  // Keep mapping ids only as soft hints — TaskApi ids may differ; form matches by name.
  const stateId = mappedStateId || existing.state_id || "";
  const cityId = mappedCityId || existing.city_id || "";

  const next = {
    ...existing,
    firstName,
    lastName,
    first_name: firstName,
    middle_name: lastName,
    last_name: lastName,
    pincode: pincode || existing.pincode || "",
    state: state || existing.state || "",
    district: district || existing.district || "",
    gender: gender || existing.gender || "",
    dob: dob || existing.dob || "",
    mobile: mobile || existing.mobile || "",
    mobile_verified: true,
    address,
    state_id: stateId,
    city_id: cityId,
    city: district || existing.city || "",
    city_name: district || existing.city_name || "",
    email: existing.email || existing.email_personal || "",
    email_personal: existing.email_personal || existing.email || "",
  };

  localStorage.setItem("loanFormData", JSON.stringify(next));
  return next;
};
